import React, { useState } from "react";
import { ArrowRight, Check, X, Gift, Sparkles } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PackageOrderDialog from "../components/PackageOrderDialog";
import { useLang } from "../i18n/LangContext";
import { getPricingT, PRICING_PRICES } from "../i18n/pricing_i18n";

const NAVY = "#1B2A4A";
const GOLD = "#C9A96E";
const GOLD_LIGHT = "#E8D5B0";
const GOLD_PALE = "#F7F1E8";
const BORDER = "#E8E0D4";
const TEXT = "#4A4540";
const MUTED = "#7A7470";

// ─────────────────────── SUB-COMPONENTS ───────────────────────
const YearTabs = ({ year, setYear }) => (
  <div className="inline-flex items-center gap-1 p-1 rounded-xl mb-8" style={{ background: GOLD_PALE, border: `1px solid ${BORDER}` }} data-testid="year-toggle">
    {[2026, 2027, 2028].map((y) => (
      <button
        key={y}
        onClick={() => setYear(y)}
        className="px-5 py-2 rounded-lg text-sm font-medium transition"
        style={{ background: year === y ? NAVY : "transparent", color: year === y ? "#F5EFE6" : MUTED }}
        data-testid={`year-tab-${y}`}
      >
        {y}
      </button>
    ))}
  </div>
);

const PkgCard = ({ tier, year, price, t, featured, onOrder }) => {
  const f = featured;
  const fullName = `${t.chooseBtn} ${tier.name}`;
  return (
    <div className="relative p-8" style={{ background: f ? NAVY : "#fff" }} data-testid={`pkg-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}>
      {f && (
        <span className="absolute top-0 right-6 px-3 py-1.5 text-[10px] font-semibold tracking-[0.1em] uppercase rounded-b-md" style={{ background: GOLD, color: NAVY }}>
          {t.popular}
        </span>
      )}
      <p className="font-display text-[22px] mb-1.5" style={{ color: f ? "#F5EFE6" : NAVY, fontWeight: 500 }}>{tier.name}</p>
      <p className="text-[13px] leading-relaxed mb-5 font-light" style={{ color: f ? "rgba(245,239,230,0.6)" : MUTED }}>{tier.desc}</p>
      <p className="font-display text-[36px] leading-none" style={{ color: f ? "#F5EFE6" : NAVY, fontWeight: 500 }}>
        {price[year]}
      </p>
      <p className="text-xs mt-1 mb-6" style={{ color: f ? "rgba(245,239,230,0.5)" : MUTED }}>{t.discountNote}</p>
      <div className="h-px mb-5" style={{ background: f ? "rgba(255,255,255,0.1)" : BORDER }} />
      <div className="space-y-2">
        {tier.features.map((feat, i) => (
          <div key={i} className="flex items-start gap-2.5 text-[13px] leading-snug" style={{ color: f ? "rgba(245,239,230,0.88)" : TEXT }}>
            <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
            <span>{feat}</span>
          </div>
        ))}
        {(tier.muted || []).map((feat, i) => (
          <div key={`m-${i}`} className="flex items-start gap-2.5 text-[13px] leading-snug line-through opacity-40" style={{ color: f ? "rgba(245,239,230,0.6)" : MUTED }}>
            <X className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>{feat}</span>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onOrder(fullName)} className="block w-full mt-7 py-3.5 text-center rounded-lg text-sm font-medium tracking-wide transition hover:opacity-90 cursor-pointer" style={{ background: f ? GOLD : "transparent", color: f ? NAVY : GOLD, border: `1px solid ${GOLD}` }} data-testid={`pkg-cta-${tier.name.toLowerCase().replace(/\s+/g, "-")}`}>
        {fullName}
      </button>
    </div>
  );
};

const PkgGrid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 rounded-2xl overflow-hidden" style={{ border: `1px solid ${BORDER}`, gap: "1px", background: BORDER }}>
    {children}
  </div>
);

const SectionHead = ({ kicker, titleA, titleB, sub }) => (
  <div className="mb-10">
    <span className="inline-block text-[10px] font-semibold tracking-[0.18em] uppercase px-3.5 py-1.5 rounded-full mb-7" style={{ color: GOLD, border: `1px solid ${GOLD_LIGHT}` }}>{kicker}</span>
    <h2 className="font-display text-3xl md:text-4xl" style={{ color: NAVY, fontWeight: 400, letterSpacing: "-0.01em" }}>
      {titleA} <em style={{ color: GOLD, fontStyle: "italic" }}>{titleB}</em>
    </h2>
    {sub && <p className="text-sm mt-2 font-light" style={{ color: MUTED }}>{sub}</p>}
    <div className="w-10 h-px mt-5" style={{ background: GOLD }} />
  </div>
);

const UniGroup = ({ groupIdx, kicker, group, prices, t, onOrder }) => {
  const [year, setYear] = useState(2026);
  return (
    <div className={groupIdx > 0 ? "mt-14" : ""}>
      <p className="text-xs uppercase tracking-[0.15em] mb-5 font-semibold" style={{ color: GOLD }}>{kicker}</p>
      <YearTabs year={year} setYear={setYear} />
      <PkgGrid>
        <PkgCard tier={group.silver} year={year} price={prices.silver} t={t} onOrder={onOrder} />
        <PkgCard tier={group.gold} year={year} price={prices.gold} t={t} onOrder={onOrder} featured />
        <PkgCard tier={group.premium} year={year} price={prices.premium} t={t} onOrder={onOrder} />
      </PkgGrid>
    </div>
  );
};

// ─────────────────────── PAGE ───────────────────────
const PricingPage = () => {
  const { lang } = useLang();
  const t = getPricingT(lang);
  const rtl = lang === 'ar';
  const [schoolYear, setSchoolYear] = useState(2026);
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderPkg, setOrderPkg] = useState("");
  const openOrder = (pkg = "") => { setOrderPkg(pkg); setOrderOpen(true); };

  return (
    <div className="min-h-screen" style={{ background: "#FDFCFA" }} dir={rtl ? "rtl" : "ltr"}>
      <Header onOpenForm={() => openOrder("")} />

      {/* HERO */}
      <section className="relative overflow-hidden pt-36 pb-20 px-5 md:px-8 text-center" style={{ background: NAVY }} data-testid="pricing-hero">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(201,169,110,0.15) 0%, transparent 70%)" }} />
        <p className="text-[11px] font-medium tracking-[0.22em] uppercase mb-5 relative" style={{ color: GOLD }}>{t.breadcrumb}</p>
        <h1 className="font-display relative text-balance" style={{ color: "#F5EFE6", fontWeight: 300, fontSize: "clamp(38px,6vw,60px)", lineHeight: 1.1, letterSpacing: "-0.01em" }}>
          {t.heroTitleA} <span style={{ color: GOLD_LIGHT }}>{t.heroTitleB}</span>
        </h1>
        <p className="mt-5 max-w-xl mx-auto text-base md:text-[17px] leading-relaxed relative" style={{ color: "rgba(245,239,230,0.92)", fontWeight: 400 }}>{t.heroSub}</p>
      </section>

      {/* SECTION 1 — UNIVERSITIES */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-20">
        <SectionHead kicker={t.sec1.kicker} titleA={t.sec1.titleA} titleB={t.sec1.titleB} sub={t.sec1.sub} />
        <UniGroup groupIdx={0} kicker={t.sec1g1} group={t.uni.g1} prices={PRICING_PRICES.uni.g1} t={t} onOrder={openOrder} />
        <UniGroup groupIdx={1} kicker={t.sec1g2} group={t.uni.g2} prices={PRICING_PRICES.uni.g2} t={t} onOrder={openOrder} />
        <UniGroup groupIdx={2} kicker={t.sec1g3} group={t.uni.g3} prices={PRICING_PRICES.uni.g3} t={t} onOrder={openOrder} />
      </section>

      <div className="h-px max-w-6xl mx-auto" style={{ background: BORDER }} />

      {/* SECTION 2 — BOARDING SCHOOLS */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-20">
        <SectionHead kicker={t.sec2.kicker} titleA={t.sec2.titleA} titleB={t.sec2.titleB} sub={t.sec2.sub} />
        <YearTabs year={schoolYear} setYear={setSchoolYear} />
        <PkgGrid>
          <PkgCard tier={t.schools.gold} year={schoolYear} price={PRICING_PRICES.schools.gold} t={t} onOrder={openOrder} />
          <PkgCard tier={t.schools.premium} year={schoolYear} price={PRICING_PRICES.schools.premium} t={t} onOrder={openOrder} featured />
          <PkgCard tier={t.schools.alina} year={schoolYear} price={PRICING_PRICES.schools.alina} t={t} onOrder={openOrder} />
        </PkgGrid>
      </section>

      <div className="h-px max-w-6xl mx-auto" style={{ background: BORDER }} />

      {/* SECTION 3 — INDIVIDUAL SERVICES */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-20">
        <SectionHead kicker={t.sec3.kicker} titleA={t.sec3.titleA} titleB={t.sec3.titleB} sub={t.sec3.sub} />
        <div className="space-y-6">
          {[
            { key: 'career', price: PRICING_PRICES.solo.career },
            { key: 'brand', price: PRICING_PRICES.solo.brand },
            { key: 'summer', price: PRICING_PRICES.solo.summer },
          ].map(({ key, price }) => {
            const s = t.solo[key];
            return (
              <div key={key} className="rounded-2xl p-8 md:p-9 flex flex-col md:flex-row gap-8 items-start" style={{ background: GOLD_PALE, border: `1px solid ${BORDER}` }} data-testid={`service-${key}`}>
                <div className="flex-1">
                  <p className="font-display text-[22px] mb-2" style={{ color: NAVY, fontWeight: 500 }}>{s.title}</p>
                  <p className="text-[13px] leading-relaxed mb-4 font-light" style={{ color: MUTED }}>{s.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {s.tags.map((tg) => (
                      <span key={tg} className="text-[11px] px-3 py-1 rounded-full bg-white" style={{ color: "#3D3830", border: `1px solid ${BORDER}` }}>{tg}</span>
                    ))}
                  </div>
                </div>
                <div className="md:text-right md:min-w-[200px]">
                  <p className="font-display text-[34px]" style={{ color: NAVY, fontWeight: 500 }}>{price}</p>
                  <p className="text-xs mt-1" style={{ color: MUTED }}>{s.note}</p>
                  <button type="button" onClick={() => openOrder(s.title)} className="inline-block mt-4 px-6 py-2.5 rounded-lg text-sm font-medium transition hover:opacity-90 cursor-pointer" style={{ border: `1px solid ${GOLD}`, color: GOLD, background: 'transparent' }} data-testid={`service-cta-${key}`}>{t.orderBtn}</button>
                </div>
              </div>
            );
          })}

          {/* VISA SUPPORT */}
          <div className="grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden" style={{ border: `1px solid ${BORDER}`, gap: "2px", background: BORDER }} data-testid="service-visa">
            <div className="p-10" style={{ background: NAVY, color: "#F5EFE6" }}>
              <p className="font-display text-[26px] mb-3" style={{ fontWeight: 400 }}>{t.visa.title} <em style={{ color: GOLD_LIGHT, fontStyle: "italic" }}>{t.visa.titleEm}</em></p>
              <p className="text-[13px] leading-relaxed font-light" style={{ color: "rgba(245,239,230,0.6)" }}>{t.visa.desc}</p>
              <p className="mt-6 text-[11px] tracking-[0.12em] uppercase mb-3" style={{ color: GOLD }}>{t.visa.kicker}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <p className="text-[10px] mb-1" style={{ color: "rgba(245,239,230,0.45)" }}>{t.visa.ukLabel}</p>
                  <p className="font-display text-[22px]" style={{ color: "#F5EFE6" }}>{PRICING_PRICES.visa.uk} <span className="text-sm font-sans" style={{ color: "rgba(245,239,230,0.45)" }}>{t.visa.stud}</span></p>
                </div>
                <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <p className="text-[10px] mb-1" style={{ color: "rgba(245,239,230,0.45)" }}>{t.visa.usaLabel}</p>
                  <p className="font-display text-[22px]" style={{ color: "#F5EFE6" }}>{PRICING_PRICES.visa.usa} <span className="text-sm font-sans" style={{ color: "rgba(245,239,230,0.45)" }}>{t.visa.stud}</span></p>
                </div>
                <div className="rounded-xl p-4 col-span-2" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <p className="text-[10px] mb-1" style={{ color: "rgba(245,239,230,0.45)" }}>{t.visa.nlLabel}</p>
                  <p className="font-display text-[22px]" style={{ color: GOLD_LIGHT }}>{t.visa.nlPrice} <Gift className="inline w-5 h-5 ml-1" /></p>
                </div>
              </div>
            </div>
            <div className="p-9 bg-white">
              <ul className="space-y-2">
                {t.visa.features.map((tx) => (
                  <li key={tx} className="text-[13px] flex items-start gap-2.5 py-2 border-b" style={{ color: TEXT, borderColor: GOLD_PALE }}>
                    <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
                    <span>{tx}</span>
                  </li>
                ))}
                <li className="text-xs pt-2" style={{ color: MUTED }}>{t.visa.footnote}</li>
              </ul>
              <button type="button" onClick={() => openOrder(t.visa.title + ' ' + t.visa.titleEm)} className="block w-full mt-6 py-3.5 text-center rounded-lg text-sm font-medium transition hover:opacity-90 cursor-pointer" style={{ background: GOLD, color: NAVY, border: 'none' }} data-testid="visa-cta">{t.visa.cta}</button>
            </div>
          </div>
        </div>
      </section>

      <div className="h-px max-w-6xl mx-auto" style={{ background: BORDER }} />

      {/* SECTION 4 — 2-YEAR PROGRAM */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-20">
        <SectionHead kicker={t.sec4.kicker} titleA={t.sec4.titleA} titleB={t.sec4.titleB} sub={t.sec4.sub} />
        <div className="grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden" style={{ border: `1px solid ${BORDER}`, gap: "2px", background: BORDER }} data-testid="two-year-program">
          <div className="p-10" style={{ background: NAVY, color: "#F5EFE6" }}>
            <p className="font-display text-[26px] leading-tight" style={{ fontWeight: 400 }}>
              {t.twoYear.titleA}<br />
              <em style={{ color: GOLD_LIGHT, fontStyle: "italic" }}>{t.twoYear.titleB}</em>
            </p>
            <p className="mt-3 text-[13px] leading-relaxed font-light" style={{ color: "rgba(245,239,230,0.6)" }}>{t.twoYear.desc1}</p>
            <p className="mt-3 text-[13px] leading-relaxed font-light" style={{ color: "rgba(245,239,230,0.6)" }}>{t.twoYear.desc2}</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.06)" }}>
                <p className="text-[10px] mb-1" style={{ color: "rgba(245,239,230,0.45)" }}>{t.twoYear.y1Label}</p>
                <p className="font-display text-[24px]" style={{ color: "#F5EFE6" }}>{PRICING_PRICES.twoYear.y1}</p>
              </div>
              <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.06)" }}>
                <p className="text-[10px] mb-1" style={{ color: "rgba(245,239,230,0.45)" }}>{t.twoYear.y2Label}</p>
                <p className="font-display text-[24px]" style={{ color: "#F5EFE6" }}>{PRICING_PRICES.twoYear.y2}</p>
              </div>
            </div>
            <div className="mt-7 flex items-baseline gap-3">
              <span className="text-sm line-through" style={{ color: "rgba(245,239,230,0.4)" }}>{PRICING_PRICES.twoYear.oldTotal}</span>
              <span className="font-display text-[32px]" style={{ color: GOLD_LIGHT, fontWeight: 500 }}>{PRICING_PRICES.twoYear.total}</span>
              <span className="text-xs" style={{ color: "rgba(245,239,230,0.4)" }}>{t.twoYear.specialNote}</span>
            </div>
          </div>
          <div className="p-9 bg-white">
            <ul className="space-y-2">
              {t.twoYear.features.map((tx) => (
                <li key={tx} className="text-[13px] flex items-start gap-2.5 py-2 border-b" style={{ color: TEXT, borderColor: GOLD_PALE }}>
                  <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
                  <span>{tx}</span>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => openOrder(`${t.twoYear.titleA} ${t.twoYear.titleB}`)} className="block w-full mt-6 py-3.5 text-center rounded-lg text-sm font-medium transition hover:opacity-90 cursor-pointer" style={{ background: GOLD, color: NAVY, border: 'none' }} data-testid="two-year-cta">{t.twoYear.cta}</button>
          </div>
        </div>
      </section>

      <div className="h-px max-w-6xl mx-auto" style={{ background: BORDER }} />

      {/* SECTION 5 — POST-ENROLLMENT SUPPORT */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-20">
        <SectionHead kicker={t.sec5.kicker} titleA={t.sec5.titleA} titleB={t.sec5.titleB} sub={t.sec5.sub} />
        <div className="grid grid-cols-1 md:grid-cols-3 rounded-2xl overflow-hidden" style={{ border: `1px solid ${BORDER}`, gap: "1px", background: BORDER }} data-testid="support-grid">
          {[
            { key: 'basic', price: `${PRICING_PRICES.support.basic} ${t.support.basic.priceSuffix}`, dark: true, data: t.support.basic },
            { key: 'standard', price: `${PRICING_PRICES.support.standard} ${t.support.standard.priceSuffix}`, data: t.support.standard },
            { key: 'premium', price: `${PRICING_PRICES.support.premium} ${t.support.premium.priceSuffix}`, data: t.support.premium },
          ].map((s) => (
            <div key={s.key} className="p-8" style={{ background: s.dark ? NAVY : "#fff" }}>
              <p className="font-display text-[20px] mb-1.5" style={{ color: s.dark ? "#F5EFE6" : NAVY, fontWeight: 500 }}>{s.data.name}</p>
              <p className="font-display text-[24px] mb-5" style={{ color: GOLD, fontWeight: 500 }}>{s.price}</p>
              <div className="space-y-2">
                {s.data.features.map((f, i) => (
                  <div key={i} className="text-[12.5px] flex items-start gap-2 py-1.5 border-b leading-relaxed" style={{ color: s.dark ? "rgba(245,239,230,0.75)" : TEXT, borderColor: s.dark ? "rgba(255,255,255,0.08)" : GOLD_PALE }}>
                    <span style={{ color: GOLD }}>·</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => openOrder(`${t.chooseBtn} ${s.data.name}`)} className="block w-full mt-6 py-3 text-center rounded-lg text-sm font-medium transition hover:opacity-90 cursor-pointer" style={{ background: s.dark ? GOLD : "transparent", border: `1px solid ${GOLD}`, color: s.dark ? NAVY : GOLD }} data-testid={`support-cta-${s.key}`}>
                {t.chooseBtn} {s.data.name}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* NOTE BAR */}
      <div className="px-5 md:px-8 py-6 flex flex-wrap items-center justify-center gap-3 text-center" style={{ background: GOLD_PALE, borderTop: `1px solid ${BORDER}` }} data-testid="note-bar">
        <Sparkles className="w-5 h-5" style={{ color: GOLD }} />
        <span className="text-sm" style={{ color: MUTED }}>
          <strong style={{ color: NAVY, fontWeight: 500 }}>{t.noteBar.a}</strong> {t.noteBar.b}
        </span>
      </div>

      <Footer />
      <PackageOrderDialog open={orderOpen} onOpenChange={setOrderOpen} packageName={orderPkg} />
    </div>
  );
};

export default PricingPage;
